import axios from "axios";
import { handleSessionResponseError } from "../utils/sessionExpiration";
import {
  buildRequestBurstKey,
  coalesceRequestBurst,
} from "../utils/requestBurstDedupe";

export const AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  }
});

AxiosInstance.interceptors.request.use(
  (config) => {

    const token = localStorage.getItem("token");
    if(token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      if (typeof config.headers?.delete === "function") {
        config.headers.delete("Content-Type");
      } else {
        delete config.headers["Content-Type"];
        delete config.headers["content-type"];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error)
  }
);

export const AxiosInstancePDF = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
  responseType: 'blob'
});

AxiosInstancePDF.interceptors.request.use(
  (config) => {

    const token = localStorage.getItem("token");
    if(token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error)
  }
);


export const AxiosPublicationsInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL_PUBLISH,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  }
});

AxiosPublicationsInstance.interceptors.request.use(
  (config) => {

    const token = localStorage.getItem("token");
    if(token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      if (typeof config.headers?.delete === "function") {
        config.headers.delete("Content-Type");
      } else {
        delete config.headers["Content-Type"];
        delete config.headers["content-type"];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error)
  }
);

export const AxiosChatInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL_CHAT,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  }
});

AxiosChatInstance.interceptors.request.use(
  (config) => {

    const token = localStorage.getItem("token");
    if(token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error)
  }
);

export const AxiosChatInstanceTwo = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL_CHAT,
  timeout: 60000,
  headers: {
    "Content-Type": "multipart/form-data",
  }
});

AxiosChatInstanceTwo.interceptors.request.use(
  (config) => {

    const token = localStorage.getItem("token");
    if(token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error)
  }
);

[
  AxiosInstance,
  AxiosInstancePDF,
  AxiosPublicationsInstance,
  AxiosChatInstance,
  AxiosChatInstanceTwo,
].forEach((instance) => {
  instance.interceptors.response.use((response) => response, handleSessionResponseError);
});

const READ_ONLY_POST_ENDPOINTS = new Set([
  "/api/get/postcat",
  "/api/get/postid",
  "/api/gral/commentpost",
  "/api/obtener/historial-reserva",
  "/api/mostrar/detalles/cupon",
  "/api/mostrar/detalles/cupon-panel",
  "/api/listado/cupones-panel",
  "/api/image/pull",
  "/api/obtener/permisos-interaccion",
  "/api/pubs/getcomments",
  "/api/feed/publicaciones-hashtag",
  "/api/obtener/busqueda-housing",
  "/api/busqueda/top5-cercanos",
  "/api/mostrar/tarjeta-pin",
  "/api/obtener/detalles-pin",
  "/api/feed/eventosweb",
  "/api/detalles/eventos",
  "/api/detalles/eventoST",
  "/api/get/eventosporusuario",
  "/api/buscador/chat-amigos",
  "/api/motor/busqueda-general",
  "/api/obtener/evidencias-propietario",
  "/api/get/feed-inmueblesgral",
  "/api/get/detalles-inmueble",
  "/api/obtener/inmuebles-favoritos",
  "/api/get/feed-inmuebles",
  "/api/mostrar/proceso-arrendamiento",
]);

const normalizeEndpointPath = (url) => {
  const value = String(url || "");
  const queryIndex = value.indexOf("?");
  return queryIndex >= 0 ? value.slice(0, queryIndex) : value;
};

const attachRequestBurstProtection = (instance, namespace) => {
  const originalGet = instance.get.bind(instance);
  const originalPost = instance.post.bind(instance);

  instance.get = (url, config = {}) => {
    const {
      skipRequestDedupe = false,
      requestDedupeWindowMs = 150,
      forceRefresh = false,
      ...axiosConfig
    } = config || {};

    const bypass =
      skipRequestDedupe === true ||
      forceRefresh === true ||
      Boolean(axiosConfig.signal) ||
      Boolean(axiosConfig.cancelToken);

    const token =
      typeof localStorage !== "undefined" ? localStorage.getItem("token") : "";

    const key = buildRequestBurstKey({
      namespace,
      method: "GET",
      url,
      params: axiosConfig.params,
      token,
      responseType: axiosConfig.responseType,
      headers: axiosConfig.headers,
    });

    return coalesceRequestBurst(
      key,
      () => originalGet(url, axiosConfig),
      { windowMs: requestDedupeWindowMs, bypass },
    );
  };

  instance.post = (url, data, config = {}) => {
    const endpointPath = normalizeEndpointPath(url);

    if (!READ_ONLY_POST_ENDPOINTS.has(endpointPath)) {
      return originalPost(url, data, config);
    }

    const {
      skipRequestDedupe = false,
      requestDedupeWindowMs = 150,
      forceRefresh = false,
      ...axiosConfig
    } = config || {};

    const bypass =
      skipRequestDedupe === true ||
      forceRefresh === true ||
      Boolean(axiosConfig.signal) ||
      Boolean(axiosConfig.cancelToken);

    const token =
      typeof localStorage !== "undefined" ? localStorage.getItem("token") : "";

    const key = buildRequestBurstKey({
      namespace,
      method: "POST_READ",
      url: endpointPath,
      data,
      token,
      responseType: axiosConfig.responseType,
      headers: axiosConfig.headers,
    });

    return coalesceRequestBurst(
      key,
      () => originalPost(url, data, axiosConfig),
      { windowMs: requestDedupeWindowMs, bypass },
    );
  };
};

attachRequestBurstProtection(AxiosInstance, "core");
attachRequestBurstProtection(AxiosPublicationsInstance, "publications");
attachRequestBurstProtection(AxiosChatInstance, "chat");
